/**
 * Find a record by its number - GET v3/loans/admin/find?id=N (action find.records).
 * Looks across write-ups, the archive, adverse actions, withdrawn and drafts; a write-up hit opens in the LWP dashboard.
 */

import { useState } from 'react';
import { Banner, Button, Pill, Spinner } from '@/components/primitives';
import { DataTable } from '@/components/DataTable';
import { TextField } from '@/components/FormField';
import { loansApi } from '../../loansApi';
import { errorText, fmtCt, lwpAdverseLink, lwpArchiveLink, lwpDraftLink, lwpLoanLink } from '../common';

interface FindHit {
  kind: string;
  id: number;
  title: string;
  status: string;
  date: string | null;
  owner: string | null;
  writeupId: number | null;
  error: string | null;
}
interface FindResult { id: number; hits: FindHit[] }

// Which hits the dashboard can open, and how (LWP 1.517.0 deep links). The ARCHIVE numbers its rows separately from the active list,
// so an archived id goes as ?archItem=, never ?loanItem= (that would open a different loan with the same number). A promoted draft
// opens as its write-up. Withdrawn rows have no dashboard landing.
function lwpLink(h: FindHit): { href: string; label: string } | null {
  if (h.writeupId) return { href: lwpLoanLink(h.writeupId), label: `Open WU-${h.writeupId}` };
  if (/^writeups?$/i.test(h.kind)) return { href: lwpLoanLink(h.id), label: `Open WU-${h.id}` };
  if (/^archived?$/i.test(h.kind)) return { href: lwpArchiveLink(h.id), label: `Open archived WU-${h.id}` };
  if (/^adverse/i.test(h.kind)) return { href: lwpAdverseLink(h.id), label: `Open adverse #${h.id}` };
  if (/^drafts?$/i.test(h.kind)) return { href: lwpDraftLink(h.id), label: `Open draft #${h.id}` };
  return null;
}

export function LoansFindPage(): JSX.Element {
  const [id, setId] = useState('');
  const [result, setResult] = useState<FindResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const n = Number(id.trim().replace(/^(WU|APP|ADV)-?/i, ''));
  const valid = Number.isInteger(n) && n > 0;

  const run = async (): Promise<void> => {
    if (!valid) return;
    setLoading(true);
    setError(null);
    try {
      setResult(await loansApi.get<FindResult>('v3/loans/admin/find', { id: n }));
    } catch (e) {
      setResult(null);
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2>Find a record</h2>
      <p className="page-intro">
        Type a number (a write-up id like 4572 or WU-4572). Every list that uses that number is searched: write-ups, the archive,
        adverse actions, withdrawn and drafts. A write-up opens in the pipeline dashboard.
      </p>
      <form
        className="toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <TextField label="Number" value={id} onChange={setId} placeholder="4572" monospace />
        <Button type="submit" disabled={!valid || loading}>
          {loading ? 'Searching…' : 'Find'}
        </Button>
      </form>
      {error && <Banner tone="err">{error}</Banner>}
      {loading && !result && <Spinner />}
      {result && (
        <DataTable
          rows={result.hits}
          rowKey={(h) => `${h.kind}-${h.id}`}
          emptyState={<div className="empty">Nothing uses number {result.id}.</div>}
          columns={[
            { header: 'Where', cell: (h) => <Pill tone="info">{h.kind}</Pill> },
            { header: 'Id', cell: (h) => h.id },
            { header: 'Title', cell: (h) => h.title || '—' },
            { header: 'Status', cell: (h) => h.status || '—' },
            { header: 'Date', cell: (h) => fmtCt(h.date) },
            { header: 'Owner', cell: (h) => h.owner ?? '—' },
            {
              header: 'Open',
              cell: (h) => {
                if (h.error) return <span className="form-field__error">{h.error}</span>;
                const link = lwpLink(h);
                return link ? (
                  <a href={link.href} target="_blank" rel="noreferrer">
                    {link.label}
                  </a>
                ) : (
                  '—'
                );
              },
            },
          ]}
        />
      )}
    </div>
  );
}
